import { apiGet, apiPatch, apiPost } from '../client';
import {
  CustomerSubscriptionListResponseSchema,
  SellSubscriptionResponseSchema,
  SubscriptionPlanListResponseSchema,
  SubscriptionPlanResponseSchema,
  type ChargePaymentMethod,
  type CustomerSubscription,
  type SubscriptionPlan,
  type SubscriptionPlanKind,
} from '../schemas';

/** Owner-only Planes (migration 0028). Retired plans stay readable. */
export function listSubscriptionPlans(includeInactive: boolean): Promise<{ data: SubscriptionPlan[] }> {
  const query = includeInactive ? '?includeInactive=true' : '';
  return apiGet(`/v1/business/subscription-plans${query}`, SubscriptionPlanListResponseSchema);
}

export type CreatePlanInput = {
  name: string;
  kind: SubscriptionPlanKind;
  creditCount?: number;
  priceCents: number;
  validityDays: number;
};

export async function createSubscriptionPlan(
  input: CreatePlanInput,
  idempotencyKey: string,
): Promise<SubscriptionPlan> {
  const response = await apiPost(
    '/v1/business/subscription-plans',
    SubscriptionPlanResponseSchema,
    input,
    idempotencyKey,
  );
  return response.data;
}

export type PatchPlanInput = Partial<{
  name: string;
  creditCount: number;
  priceCents: number;
  validityDays: number;
  isActive: boolean;
  /** The version the form read; a stale one comes back 409 version_conflict. */
  expectedVersion: number;
}>;

/** Also how a plan is retired: `isActive: false`, never a delete. */
export async function patchSubscriptionPlan(
  planId: string,
  input: PatchPlanInput,
  idempotencyKey: string,
): Promise<SubscriptionPlan> {
  const response = await apiPatch(
    `/v1/business/subscription-plans/${encodeURIComponent(planId)}`,
    SubscriptionPlanResponseSchema,
    input,
    idempotencyKey,
  );
  return response.data;
}

export function listCustomerSubscriptions(
  customerId: string,
): Promise<{ data: CustomerSubscription[] }> {
  return apiGet(
    `/v1/business/customers/${encodeURIComponent(customerId)}/subscriptions`,
    CustomerSubscriptionListResponseSchema,
  );
}

/** Selling is also how a renewal happens: another sale of the same plan. */
export async function sellSubscription(
  customerId: string,
  input: { planId: string; paymentMethod: ChargePaymentMethod },
  idempotencyKey: string,
) {
  return apiPost(
    `/v1/business/customers/${encodeURIComponent(customerId)}/subscriptions`,
    SellSubscriptionResponseSchema,
    input,
    idempotencyKey,
  );
}
